import type { Question, QuestionType, AnswerValue } from '@/types';

// Import all question components
import { SingleSelect } from './single-select';
import { MultiSelect } from './multi-select';
import { Dropdown } from './dropdown';
import { ImageSelect } from './image-select';
import { OpenText } from './open-text';
import { OpenNumeric } from './open-numeric';
import { NPS } from './nps';
import { Rating } from './rating';
import { SliderQuestion } from './slider-question';
import { Likert } from './likert';
import { MatrixSingle } from './matrix-single';
import { MatrixMulti } from './matrix-multi';
import { MatrixScale } from './matrix-scale';
import { Ranking } from './ranking';
import { MaxDiff } from './max-diff';
import { Video } from './video';
import { Info } from './info';
import { DatePicker } from './date-picker';
import { FileUpload } from './file-upload';

interface QuestionRendererProps {
  question: Question;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  onFocus?: () => void;
  disabled?: boolean;
  errors?: string[];
  displayOrder?: number[];
}

/**
 * Renders the appropriate question component based on question type.
 */
export function QuestionRenderer({
  question,
  value,
  onChange,
  disabled,
  errors,
  displayOrder,
}: QuestionRendererProps) {
  const commonProps = {
    question,
    disabled,
    errors,
    displayOrder,
  };

  switch (question.type) {
    // Selection types
    case 'single_select':
      return (
        <SingleSelect
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    case 'multi_select':
      return (
        <MultiSelect
          {...commonProps}
          value={value as string[] | null}
          onChange={onChange as (v: string[] | null) => void}
        />
      );

    case 'dropdown':
      return (
        <Dropdown
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    case 'image_select':
      return (
        <ImageSelect
          {...commonProps}
          value={value as string | string[] | null}
          onChange={onChange as (v: string | string[] | null) => void}
        />
      );

    // Text types
    case 'open_text':
      return (
        <OpenText
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    case 'open_numeric':
      return (
        <OpenNumeric
          {...commonProps}
          value={value as number | null}
          onChange={onChange as (v: number | null) => void}
        />
      );

    // Scale types
    case 'nps':
      return (
        <NPS
          {...commonProps}
          value={value as number | null}
          onChange={onChange as (v: number | null) => void}
        />
      );

    case 'rating':
      return (
        <Rating
          {...commonProps}
          value={value as number | null}
          onChange={onChange as (v: number | null) => void}
        />
      );

    case 'slider':
      return (
        <SliderQuestion
          {...commonProps}
          value={value as number | null}
          onChange={onChange as (v: number | null) => void}
        />
      );

    case 'likert':
      return (
        <Likert
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    // Matrix types
    case 'matrix_single':
      return (
        <MatrixSingle
          {...commonProps}
          value={value as Record<string, string> | null}
          onChange={onChange as (v: Record<string, string> | null) => void}
        />
      );

    case 'matrix_multi':
      return (
        <MatrixMulti
          {...commonProps}
          value={value as Record<string, string[]> | null}
          onChange={onChange as (v: Record<string, string[]> | null) => void}
        />
      );

    case 'matrix_scale':
      return (
        <MatrixScale
          {...commonProps}
          value={value as Record<string, number> | null}
          onChange={onChange as (v: Record<string, number> | null) => void}
        />
      );

    // Ranking types
    case 'ranking':
      return (
        <Ranking
          {...commonProps}
          value={value as string[] | null}
          onChange={onChange as (v: string[] | null) => void}
        />
      );

    case 'max_diff':
      return (
        <MaxDiff
          {...commonProps}
          value={value as import('@/types').MaxDiffAnswer | null}
          onChange={onChange as (v: import('@/types').MaxDiffAnswer | null) => void}
        />
      );

    // Media types
    case 'video':
      return (
        <Video
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    // Special types
    case 'info':
      return (
        <Info
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    case 'date':
    case 'datetime':
      return (
        <DatePicker
          {...commonProps}
          value={value as string | null}
          onChange={onChange as (v: string | null) => void}
        />
      );

    case 'file_upload':
      return (
        <FileUpload
          {...commonProps}
          value={value as import('@/types').FileUploadAnswer[] | null}
          onChange={onChange as (v: import('@/types').FileUploadAnswer[] | null) => void}
        />
      );

    default:
      // Fallback for unknown types
      return (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">
            Unknown question type: <code className="font-mono">{question.type}</code>
          </p>
        </div>
      );
  }
}

/**
 * Get display name for a question type.
 */
export function getQuestionTypeLabel(type: QuestionType): string {
  const labels: Record<QuestionType, string> = {
    single_select: 'Single Select',
    multi_select: 'Multi Select',
    dropdown: 'Dropdown',
    open_text: 'Open Text',
    open_numeric: 'Numeric',
    nps: 'NPS',
    rating: 'Rating',
    slider: 'Slider',
    likert: 'Likert Scale',
    matrix_single: 'Matrix (Single)',
    matrix_multi: 'Matrix (Multi)',
    matrix_scale: 'Matrix (Scale)',
    ranking: 'Ranking',
    max_diff: 'MaxDiff',
    image_select: 'Image Select',
    video: 'Video',
    info: 'Information',
    date: 'Date',
    datetime: 'Date & Time',
    file_upload: 'File Upload',
  };

  return labels[type] || type;
}

/**
 * Check if a question type requires an answer (vs. info-only).
 */
export function requiresAnswer(type: QuestionType): boolean {
  return type !== 'info' && type !== 'video';
}
